import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

const STORAGE_KEY = 'jt_lang';

export const SUPPORTED_LANGUAGES = ['en', 'uk', 'de', 'pt-PT', 'pt-BR', 'es-MX', 'ko', 'id', 'tr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

export interface LanguageOption {
  code: SupportedLanguage;
  label: string;
  nativeLabel: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'uk', label: 'Ukrainian', nativeLabel: 'Українська' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'pt-PT', label: 'Portuguese (Portugal)', nativeLabel: 'Português (Portugal)' },
  { code: 'pt-BR', label: 'Portuguese (Brazil)', nativeLabel: 'Português (Brasil)' },
  { code: 'es-MX', label: 'Spanish (Mexico)', nativeLabel: 'Español (México)' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe' },
];

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly translate = inject(TranslateService);

  get currentLang(): SupportedLanguage {
    return (this.translate.currentLang as SupportedLanguage) || DEFAULT_LANGUAGE;
  }

  constructor() {
    const lang = this.detect();
    localStorage.setItem(STORAGE_KEY, lang);
  }

  setLanguage(lang: string): void {
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) {
      this.translate.use(lang);
      localStorage.setItem(STORAGE_KEY, lang);
    }
  }

  private detect(): string {
    const fromQuery = new URLSearchParams(window.location.search).get('lang');
    if (fromQuery && (SUPPORTED_LANGUAGES as readonly string[]).includes(fromQuery)) {
      localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }

    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage && (SUPPORTED_LANGUAGES as readonly string[]).includes(fromStorage)) {
      return fromStorage;
    }

    const browserLocale = navigator.language;
    const browserLang = browserLocale?.split('-')[0];
    const match = (SUPPORTED_LANGUAGES as readonly string[]).find(
      (l) => l === browserLocale || l === `${browserLang}-${browserLocale?.split('-')[1]?.toUpperCase()}`,
    ) ?? (SUPPORTED_LANGUAGES as readonly string[]).find((l) => l.split('-')[0] === browserLang);
    if (match) {
      return match;
    }

    return DEFAULT_LANGUAGE;
  }
}
